/**
 * Ossature de l'application : barre latérale, barre supérieure (titre + sous-onglets + recherche),
 * et barre de navigation basse (mobile). Gère le mode « écran public » et la recherche globale.
 */
import { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { SaveIndicator } from '../components/ui/SaveIndicator';
import { CavoramaLogo } from '../components/CavoramaLogo';
import { ImpersonationBanner } from '../features/admin/Impersonate';
import { installAutoSaveDrain } from '../services/autosaveQueue';
import {
  LayoutGrid, ScanLine, Upload, Wine, LineChart, FileText, Map, Settings,
  Eye, EyeOff, Search, MoreHorizontal, RefreshCw, CloudOff, CheckCircle2, ShoppingCart, Grape, Compass, Plus, LogOut, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { usePublicMode } from '../context/PublicModeContext';
import { useOffline } from '../context/OfflineContext';
import { useNavGuard } from '../context/NavGuardContext';
import { dashboardApi, metaApi, caveApi } from '../services/api';
import { WineColorDot } from '../components/wine/wine';
import { USER_ROLE_LABEL } from '../constants/roles';
import { APP_VERSION } from '../constants/version';

const NAV = [
  { to: '/', label: 'Tableau de bord', icon: LayoutGrid, end: true },
  { to: '/scanner', label: 'Scanner', icon: ScanLine },
  { to: '/sortie', label: 'Sortie', icon: Upload },
  { to: '/cave/stock', label: 'La cave', icon: Wine, match: '/cave', exclude: ['/cave/degustes'], badge: true },
  { to: '/achats/a-traiter', label: 'Achats', icon: ShoppingCart, match: '/achats', roles: ['gerant', 'sommelier'] },
  { to: '/piloter', label: 'Piloter', icon: LineChart, match: '/piloter' },
  { divider: true },
  { to: '/carte', label: 'Carte des vins', icon: FileText, match: '/carte' },
  { to: '/vignobles', label: 'Vignobles', icon: Map, match: '/vignobles', exclude: ['/vignobles/decouvrir'] },
  { divider: true },
  { to: '/cave/degustes', label: 'Vins dégustés', icon: Grape, match: '/cave/degustes' },
  { to: '/vignobles/decouvrir', label: 'À découvrir', icon: Compass, match: '/vignobles/decouvrir' },
  { divider: true },
  { to: '/parametres/etablissement', label: 'Paramètres', icon: Settings, match: '/parametres' },
];

// Sous-onglets par section (barre supérieure).
function subtabsFor(pathname, alertCount) {
  if (pathname.startsWith('/cave')) {
    return {
      title: 'La cave',
      tabs: [
        { to: '/cave/stock', label: 'Stock' },
        { to: '/cave/alertes', label: `Alertes · ${alertCount}` },
        { to: '/cave/edition-groupee', label: 'Carte & verre' },
        { to: '/cave/mouvements', label: 'Mouvements et inventaire' },
      ],
    };
  }
  if (pathname.startsWith('/piloter')) {
    return { title: 'Piloter', tabs: [
      { to: '/piloter', label: "Vue d'ensemble", end: true },
      { to: '/piloter/valorisation', label: 'Valorisation au 31/12' },
      { to: '/piloter/rapports', label: 'Rapports' },
    ] };
  }
  if (pathname.startsWith('/vignobles')) {
    return { title: 'Vignobles', tabs: [
      { to: '/vignobles', label: 'Carte', end: true },
      { to: '/vignobles/domaines', label: 'Domaines' },
      { to: '/vignobles/mes-appellations', label: 'Appellations' },
      { to: '/vignobles/appellations', label: 'Index des appellations' },
    ] };
  }
  if (pathname.startsWith('/carte')) {
    return { title: 'Carte des vins', tabs: [
      { to: '/carte', label: 'Composer', end: true },
      { to: '/carte/traductions', label: 'Traductions' },
      { to: '/carte/editions', label: 'Éditions' },
    ] };
  }
  if (pathname.startsWith('/scanner')) return { title: 'Scanner', tabs: [] };
  if (pathname.startsWith('/achats')) {
    return { title: 'Achats', tabs: [
      { to: '/achats/a-traiter', label: 'À traiter' },
      { to: '/achats/a-commander', label: 'À commander' },
      { to: '/achats/documents', label: 'Documents' },
      { to: '/achats/commandes', label: 'Commandes' },
      { to: '/achats/fournisseurs', label: 'Fournisseurs' },
    ] };
  }
  if (pathname.startsWith('/sortie')) return { title: 'Sortie', tabs: [] };
  if (pathname.startsWith('/parametres')) return { title: 'Paramètres', tabs: [] };
  return { title: 'Tableau de bord', tabs: [] };
}

/** Rendu des résultats de recherche (partagé : recherche barre supérieure + recherche mobile). */
function SearchResultsList({ results, onGo }) {
  if (!results) return null;
  const empty = ['cuvees', 'domains', 'locations'].every((k) => !results[k]?.length);
  return (
    <div data-testid="global-search-results">
      {empty && <p className="px-3 py-4 text-sm text-n600">Aucun résultat</p>}
      {results.cuvees?.length > 0 && (
        <div>
          <p className="kicker px-3 pt-2">Cuvées</p>
          {results.cuvees.map((c) => (
            <button key={c.id} data-testid={`search-cuvee-${c.id}`} onClick={() => onGo(`/cave/cuvee/${c.id}`)}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-surface text-left">
              <WineColorDot color={c.color} size={9} />
              <span className="font-title text-lg">{c.name}</span>
              <span className="text-xs text-n500">{c.domain_name}</span>
            </button>
          ))}
        </div>
      )}
      {results.domains?.length > 0 && (
        <div className="hair-t">
          <p className="kicker px-3 pt-2">Domaines</p>
          {results.domains.map((d) => (
            <button key={d.id} onClick={() => onGo(`/vignobles/domaine/${d.id}`)}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-surface text-left">
              <span className="font-title text-lg">{d.name}</span>
              <span className="text-xs text-n500">{d.region}</span>
            </button>
          ))}
        </div>
      )}
      {results.locations?.length > 0 && (
        <div className="hair-t">
          <p className="kicker px-3 pt-2">Emplacements</p>
          {results.locations.map((l) => (
            <button key={l.id} onClick={() => onGo('/cave/stock')}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-surface text-left">
              <Map className="w-4 h-4 text-n500" strokeWidth={1.6} />
              <span className="text-[14px]">{l.name}</span>
              <span className="text-xs text-n500">{l.code}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Recherche globale avec résultats groupés (cuvées, domaines, emplacements). */
function GlobalSearch() {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState(null);
  const navigate = useNavigate();
  const { confirmLeave } = useNavGuard();
  const boxRef = useRef(null);

  const go = (path) => { if (confirmLeave()) { navigate(path); setOpen(false); setQ(''); } };

  useEffect(() => {
    if (!q.trim()) { setResults(null); return; }
    const t = setTimeout(() => {
      metaApi.search(q).then((r) => { setResults(r); setOpen(true); }).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const openFirst = () => {
    if (results?.cuvees?.[0]) go(`/cave/cuvee/${results.cuvees[0].id}`);
    else if (results?.domains?.[0]) go(`/vignobles/domaine/${results.domains[0].id}`);
  };

  return (
    <div className="relative w-full max-w-md" ref={boxRef}>
      <div className="relative">
        <Search className="w-4 h-4 text-n500 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.6} />
        <input
          data-testid="global-search-input"
          className="vd-input pl-9 h-9"
          placeholder="Cuvée, domaine, appellation…  (⌘K)"
          title="Raccourci : ⌘K / Ctrl+K"
          value={q}
          onFocus={() => results && setOpen(true)}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') openFirst(); if (e.key === 'Escape') setQ(''); }}
        />
      </div>
      {open && results && (
        <div className="absolute z-40 mt-1 w-full bg-paper border border-hair rounded-md shadow-lg overflow-hidden animate-fade-in">
          <SearchResultsList results={results} onGo={go} />
        </div>
      )}
    </div>
  );
}

/** Recherche mobile : bouton loupe (haut à droite) ouvrant un volet flottant en haut de l'écran. */
function MobileSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const navigate = useNavigate();
  const { confirmLeave } = useNavGuard();
  const inputRef = useRef(null);

  useEffect(() => {
    if (!q.trim()) { setResults(null); return; }
    const t = setTimeout(() => { metaApi.search(q).then(setResults).catch(() => {}); }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 50); }, [open]);

  const close = () => { setOpen(false); setQ(''); setResults(null); };
  const go = (path) => { if (confirmLeave()) { navigate(path); close(); } };
  const runFirst = () => {
    if (results?.cuvees?.[0]) go(`/cave/cuvee/${results.cuvees[0].id}`);
    else if (results?.domains?.[0]) go(`/vignobles/domaine/${results.domains[0].id}`);
  };

  return (
    <>
      <button onClick={() => setOpen(true)} className="sm:hidden p-2 rounded-md text-n600 hover:text-ink hover:bg-surface transition-colors" data-testid="mobile-search-btn" aria-label="Rechercher">
        <Search className="w-5 h-5" strokeWidth={1.6} />
      </button>
      {open && (
        <div className="sm:hidden fixed inset-0 z-50 bg-black/40 animate-fade-in" onClick={close} data-testid="mobile-search-overlay">
          <div className="bg-paper max-h-[40vh] flex flex-col shadow-xl animate-fade-in" onClick={(e) => e.stopPropagation()} data-testid="mobile-search-sheet">
            <div className="flex items-center gap-2 px-3 h-14 border-b border-hair shrink-0">
              <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') runFirst(); if (e.key === 'Escape') close(); }}
                placeholder="Cuvée, domaine, appellation…" className="vd-input h-10 flex-1 text-[15px]" data-testid="mobile-search-input" />
              <button onClick={runFirst} className="p-2 rounded-md text-gold700 hover:bg-surface" data-testid="mobile-search-go" aria-label="Lancer la recherche"><Search className="w-5 h-5" strokeWidth={1.7} /></button>
              <button onClick={close} className="px-2 h-10 text-[13px] text-n600 hover:text-ink" data-testid="mobile-search-cancel">Annuler</button>
            </div>
            <div className="flex-1 overflow-auto">
              <SearchResultsList results={results} onGo={go} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/** Bouton bascule du mode écran public (œil). */
function PublicModeButton({ compact }) {
  const { isPublic, forced, toggle } = usePublicMode();
  return (
    <button
      data-testid="public-mode-toggle"
      onClick={forced ? undefined : toggle}
      disabled={forced}
      className={`w-full vd-card px-3 py-2 flex items-center gap-2 text-left hover:bg-surface transition-colors justify-center xl:justify-start ${forced ? 'opacity-70' : ''}`}
      title={isPublic ? 'Données financières masquées' : 'Cliquer pour masquer (écran public)'}
    >
      {isPublic ? <EyeOff className="w-4 h-4 text-gold700 shrink-0" strokeWidth={1.6} /> : <Eye className="w-4 h-4 shrink-0" strokeWidth={1.6} />}
      {!compact && (
        <span className="leading-tight hidden xl:block">
          <span className="block text-[13px]">{isPublic ? "Prix d'achat masqués" : "Prix d'achat visibles"}</span>
          <span className="block text-[11px] text-n500">{forced ? 'Écran public (rôle serveur)' : isPublic ? 'Écran public actif' : "Cliquer pour l'écran public"}</span>
        </span>
      )}
    </button>
  );
}

/** Indicateur permanent d'état hors-ligne / synchro (bas de la barre latérale). */
function OfflineIndicator() {
  const ctx = useOffline();
  if (!ctx) return null;
  const { online, pending, syncing, lastSync, syncNow } = ctx;
  const time = lastSync ? new Date(lastSync).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : null;

  let Icon = CheckCircle2, tone = 'text-gold700', label, sub;
  if (syncing) { Icon = RefreshCw; tone = 'text-accent-700'; label = 'Synchronisation…'; sub = pending ? `${pending} en attente` : null; }
  else if (!online) { Icon = CloudOff; tone = 'text-n600'; label = pending ? `Hors-ligne · ${pending} en attente` : 'Hors-ligne prêt'; sub = pending ? 'synchro au retour du réseau' : 'données en cache'; }
  else { Icon = CheckCircle2; tone = 'text-gold700'; label = pending ? `${pending} mouvement(s) en attente` : 'Hors-ligne prêt'; sub = time ? `synchro ${time}` : 'à jour'; }

  return (
    <button
      data-testid="offline-indicator"
      onClick={online ? syncNow : undefined}
      disabled={!online || syncing}
      title={`${label}${sub ? ` · ${sub}` : ''}`}
      aria-label={`État hors-ligne : ${label}`}
      className="w-full flex items-center gap-2 px-1 xl:px-2 py-1.5 rounded-md hover:bg-surface transition-colors text-left justify-center xl:justify-start"
    >
      <Icon className={`w-4 h-4 shrink-0 ${tone} ${syncing ? 'animate-spin' : ''}`} strokeWidth={1.6} />
      <span className="hidden xl:block leading-tight min-w-0">
        <span className="block text-[11.5px] truncate">{label}</span>
        {sub && <span className="block text-[10.5px] text-n500 truncate">{sub}</span>}
      </span>
    </button>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { confirmLeave } = useNavGuard();
  const guardClick = (e) => { if (!confirmLeave()) e.preventDefault(); };
  const { data: alerts = [] } = useQuery({ queryKey: ['alerts'], queryFn: dashboardApi.alerts, staleTime: 30000 });
  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: metaApi.settings, staleTime: 60000 });
  const alertCount = alerts.length;
  const section = subtabsFor(location.pathname, alertCount);
  const [scanActive, setScanActive] = useState(0);
  const [tip, setTip] = useState(null); // bulle info (sidebar en mode icônes)
  const showTip = (e, label) => { if (window.innerWidth >= 1280) return; const r = e.currentTarget.getBoundingClientRect(); setTip({ label, top: r.top + r.height / 2 }); };
  const hideTip = () => setTip(null);

  useEffect(() => {
    const onScan = (e) => setScanActive(e.detail?.active || 0);
    window.addEventListener('scan-queue', onScan);
    return () => window.removeEventListener('scan-queue', onScan);
  }, []);

  // Rejoue les enregistrements automatiques en attente (démarrage + retour réseau).
  useEffect(() => installAutoSaveDrain(), []);

  // Raccourcis clavier globaux (éditables dans Paramètres › Raccourcis clavier).
  const scSearch = settings?.shortcuts?.search || 'mod+k';
  const scScanner = settings?.shortcuts?.scanner || 'alt+shift+c';
  useEffect(() => {
    const match = (combo, e) => {
      const parts = String(combo).toLowerCase().split('+').map((s) => s.trim()).filter(Boolean);
      if (!parts.length) return false;
      const key = parts[parts.length - 1];
      const needMod = parts.includes('mod') || parts.includes('cmd') || parts.includes('meta');
      const needCtrl = parts.includes('ctrl');
      const needAlt = parts.includes('alt') || parts.includes('option');
      const needShift = parts.includes('shift');
      if (needMod && !(e.metaKey || e.ctrlKey)) return false;
      if (!needMod && needCtrl !== e.ctrlKey) return false;
      if (needAlt !== e.altKey) return false;
      if (needShift !== e.shiftKey) return false;
      const codeMatch = key.length === 1 && /[a-z0-9]/.test(key) && e.code === (/[a-z]/.test(key) ? 'Key' : 'Digit') + key.toUpperCase();
      return e.key.toLowerCase() === key || codeMatch;
    };
    const onKey = (e) => {
      if (match(scSearch, e)) { e.preventDefault(); document.querySelector('[data-testid=global-search-input]')?.focus(); }
      else if (match(scScanner, e)) { e.preventDefault(); if (confirmLeave()) navigate('/scanner'); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [scSearch, scScanner, navigate, confirmLeave]);

  return (
    <div className="h-screen overflow-hidden flex flex-col bg-paper text-ink">
      <ImpersonationBanner />
      <div className="flex flex-1 min-h-0">
      {/* Barre latérale (desktop ≥ xl : complète ; md–xl : icônes) */}
      <aside className="hidden md:flex flex-col shrink-0 hair-r w-[72px] xl:w-[220px] py-4 px-2 xl:px-4 overflow-y-auto">
        <div className="px-1 xl:px-2 mb-6">
          <Link to="/" onClick={guardClick} className="font-title text-3xl leading-tight text-ink hidden xl:block" data-testid="brand-establishment">
            {settings?.name || 'Maison des Monts Bleus'}
          </Link>
          <span className="font-title text-2xl xl:hidden block text-center" data-testid="brand-initials">
            {(settings?.short_name || settings?.name || 'MB').split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
          </span>
          <CavoramaLogo className="h-5 mt-1.5 hidden xl:block" testid="brand-appname" />
          <p className="kicker hidden xl:block" data-testid="brand-subtitle">{settings?.subtitle || 'Cave & carte des vins'}</p>
        </div>
        <nav className="flex-1 flex flex-col gap-0.5">
          {NAV.filter((item) => item.divider || !item.roles || item.roles.includes(user?.role)).map((item, i) =>
            item.divider ? (
              <div key={i} className="my-2 mx-1 hair-b" />
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={guardClick}
                onMouseEnter={(e) => showTip(e, item.label)}
                onMouseLeave={hideTip}
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
                className={({ isActive }) => {
                  const active = item.match
                    ? location.pathname.startsWith(item.match) && !(item.exclude || []).some((e) => location.pathname.startsWith(e))
                    : isActive;
                  return `relative flex items-center gap-3 rounded-md px-2 xl:px-3 h-10 text-[14px] transition-colors justify-center xl:justify-start ${
                    active ? 'vd-selected text-gold800' : 'text-ink hover:bg-surface'
                  }`;
                }}
              >
                <item.icon className="w-[18px] h-[18px] shrink-0" strokeWidth={1.6} />
                <span className="hidden xl:block flex-1">{item.label}</span>
                {item.to === '/scanner' && scanActive > 0 && (
                  <span className="absolute xl:static right-1 top-1 min-w-[18px] h-[18px] px-1 rounded-full bg-gold text-white text-[11px] flex items-center justify-center nums animate-pulse" data-testid="scan-badge">{scanActive}</span>
                )}
                {item.badge && alertCount > 0 && (
                  <span className="hidden xl:inline text-n600 text-[13px] nums">{alertCount}</span>
                )}
              </NavLink>
            )
          )}
        </nav>        <div className="mt-4 flex flex-col gap-3">
          <PublicModeButton compact={false} />
          <OfflineIndicator />
          <div className="hidden xl:flex items-center justify-between gap-2 px-1">
            <div className="min-w-0">
              <p className="font-title text-lg leading-tight truncate">{user?.name}</p>
              <p className="text-[12px] text-n600 capitalize">{USER_ROLE_LABEL[user?.role]} · tous les droits</p>
              <p className="text-[11px] text-n500 nums" data-testid="app-version">{APP_VERSION}</p>
            </div>
            <button onClick={logout} title="Se déconnecter" aria-label="Se déconnecter"
              className="shrink-0 p-2 rounded-md text-n600 hover:text-ink hover:bg-surface transition-colors" data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
          <button onClick={logout} title="Se déconnecter" aria-label="Se déconnecter"
            onMouseEnter={(e) => showTip(e, 'Se déconnecter')} onMouseLeave={hideTip}
            className="xl:hidden mx-auto p-2 rounded-md text-n600 hover:text-ink hover:bg-surface transition-colors" data-testid="logout-btn-compact">
            <LogOut className="w-5 h-5" strokeWidth={1.6} />
          </button>
        </div>
      </aside>

      {tip && (
        <div className="fixed z-[9999] -translate-y-1/2 left-[76px] pointer-events-none rounded-md bg-ink text-paper text-[12px] px-2 py-1 whitespace-nowrap shadow-lg xl:hidden"
          style={{ top: tip.top }} data-testid="nav-tooltip">{tip.label}</div>
      )}

      {/* Zone principale */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Barre supérieure */}
        <header className="h-12 shrink-0 hair-b flex items-center gap-4 px-4 md:px-6">
          <h2 className="font-title text-xl text-ink shrink-0">{section.title}</h2>
          <nav className="hidden md:flex items-center gap-5 h-full overflow-x-auto">
            {section.tabs.map((t) => (
              <NavLink
                key={t.to}
                to={t.to}
                end={t.end}
                onClick={guardClick}
                data-testid={`subtab-${t.to.replace(/[^a-z]/gi, '-')}`}
                className={({ isActive }) =>
                  `h-full flex items-center text-[13.5px] border-b-2 -mb-px transition-colors ${
                    isActive ? 'border-gold text-ink' : 'border-transparent text-n600 hover:text-ink'
                  }`
                }
              >
                {t.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex-1 flex items-center justify-end gap-3">
            <SaveIndicator />
            <div className="hidden sm:block flex-1 max-w-md"><GlobalSearch /></div>
            <MobileSearch />
            <NavLink to="/scanner" onClick={guardClick} className="vd-btn vd-btn-primary h-9" data-testid="topbar-scanner-btn" title="Raccourci : ⌥⇧C">
              <ScanLine className="w-4 h-4" strokeWidth={1.6} /> <span className="hidden sm:inline">Scanner</span>
            </NavLink>
            <AddMenu confirmLeave={confirmLeave} />
          </div>
        </header>

        <main className="flex-1 overflow-auto pb-20 md:pb-0">{children}</main>
      </div>

      {/* Barre de navigation basse (mobile) */}
      <MobileNav alertCount={alertCount} />
      </div>
    </div>
  );
}

function MobileNav() {
  const items = [
    { to: '/', label: 'Accueil', icon: LayoutGrid, end: true },
    { to: '/sortie', label: 'Sortie', icon: Upload },
    { to: '/scanner', label: 'Scanner', icon: ScanLine, center: true },
    { to: '/cave/stock', label: 'Cave', icon: Wine, match: '/cave' },
  ];
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { confirmLeave } = useNavGuard();
  const guardClick = (e) => { if (!confirmLeave()) e.preventDefault(); };
  const [moreOpen, setMoreOpen] = useState(false);

  // Entrées « Plus » = sections de NAV absentes de la barre basse, filtrées par rôle.
  const barPaths = new Set(['/', '/sortie', '/scanner', '/cave/stock']);
  const moreItems = NAV.filter((it) => !it.divider && !barPaths.has(it.to) && (!it.roles || it.roles.includes(user?.role)));

  const goMore = (to) => { if (confirmLeave()) { navigate(to); setMoreOpen(false); } };

  return (
    <>
      <nav className="md:hidden fixed bottom-0 inset-x-0 h-16 bg-paper hair-t flex items-stretch z-30">
        {items.map((it) => {
          const active = it.match ? location.pathname.startsWith(it.match) : location.pathname === it.to;
          return (
            <NavLink key={it.to} to={it.to} onClick={guardClick} className="flex-1 flex flex-col items-center justify-center gap-0.5" data-testid={`mobilenav-${it.label.toLowerCase()}`}>
              <span className={`flex items-center justify-center ${it.center ? 'w-11 h-11 rounded-full border border-gold text-gold800 -mt-4 bg-paper' : ''}`}>
                <it.icon className={`w-5 h-5 ${active && !it.center ? 'text-gold800' : 'text-n600'}`} strokeWidth={1.6} />
              </span>
              <span className={`text-[10px] ${active ? 'text-gold800' : 'text-n600'}`}>{it.label}</span>
            </NavLink>
          );
        })}
        <button onClick={() => setMoreOpen(true)} className="flex-1 flex flex-col items-center justify-center gap-0.5" data-testid="mobilenav-plus">
          <span className="flex items-center justify-center"><MoreHorizontal className="w-5 h-5 text-n600" strokeWidth={1.6} /></span>
          <span className="text-[10px] text-n600">Plus</span>
        </button>
      </nav>

      {moreOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex items-end bg-black/40 animate-fade-in" onClick={() => setMoreOpen(false)} data-testid="mobile-more-overlay">
          <div className="w-full bg-paper rounded-t-xl shadow-xl pb-6 animate-slide-in-up max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="mobile-more-sheet">
            <div className="flex items-center justify-between px-4 h-12 border-b border-hair">
              <p className="font-title text-xl">Plus</p>
              <button onClick={() => setMoreOpen(false)} className="p-2 -mr-2 text-n600 hover:text-ink" data-testid="mobile-more-close"><X className="w-5 h-5" strokeWidth={1.6} /></button>
            </div>
            <div className="py-1">
              {moreItems.map((it) => (
                <button key={it.to} onClick={() => goMore(it.to)} data-testid={`mobile-more-${it.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-surface transition-colors">
                  <it.icon className="w-5 h-5 text-gold700 shrink-0" strokeWidth={1.6} />
                  <span className="text-[15px]">{it.label}</span>
                </button>
              ))}
              <button onClick={() => { setMoreOpen(false); logout(); }} data-testid="mobile-more-logout"
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-surface transition-colors border-t border-hair mt-1">
                <LogOut className="w-5 h-5 text-n600 shrink-0" strokeWidth={1.6} />
                <span className="text-[15px]">Se déconnecter</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/** Bouton « Ajouter » (barre supérieure) : menu de création rapide. Aujourd'hui : nouvelle bouteille. */
function AddMenu({ confirmLeave }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const boxRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const newBottle = async () => {
    if (!confirmLeave()) return;
    setOpen(false); setBusy(true);
    try {
      const r = await caveApi.createCuvee();
      navigate(`/cave/cuvee/${r.cuvee_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Création impossible'); }
    finally { setBusy(false); }
  };

  return (
    <div className="relative" ref={boxRef}>
      <button className="vd-btn vd-btn-secondary h-9" onClick={() => setOpen((o) => !o)} disabled={busy} data-testid="topbar-add-btn" title="Ajouter">
        <Plus className="w-4 h-4" strokeWidth={1.8} /> <span className="hidden sm:inline">Ajouter</span>
      </button>
      {open && (
        <div className="absolute right-0 z-40 mt-1 w-52 bg-paper border border-hair rounded-md shadow-lg overflow-hidden animate-fade-in" data-testid="topbar-add-menu">
          <button onClick={newBottle} data-testid="add-new-bottle" className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-surface text-left text-[13.5px]">
            <Wine className="w-4 h-4 text-gold700" strokeWidth={1.6} /> Nouvelle bouteille
          </button>
        </div>
      )}
    </div>
  );
}
